import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    SidebarMenuSubItem
} from "@/components/ui/sidebar";
import { useChapters, useCreateChapter, useDeleteChapter, useUpdateChapter } from '@/hooks/useChapters';
import { useDialog } from '@/hooks/useDialog';
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';

import { cn } from '@/lib/utils';
import { Edit, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export default function ChapterListComponent() {
    const { projectId, chapterId: activeChapterId } = useParams();
    const navigate = useNavigate();
    const { currentStoryId } = useProjectMeta(projectId || null);
    const storyId = currentStoryId?.toString();
    const createChapterMutation = useCreateChapter();
    const updateChapterMutation = useUpdateChapter();
    const deleteChapterMutation = useDeleteChapter();
    const { openDialog } = useDialog();

    // Use hooks to fetch chapters
    const { data: chapters = [], isLoading } = useChapters(storyId);

    // Memoize chapters
    const memoizedChapters = useMemo(() => {
        return chapters.filter(c => c.id !== undefined).map(c => ({
            ...c,
            id: c.id!
        }));
    }, [chapters]);

    const handleAddChapter = useCallback(() => {
        if (!projectId || !storyId) {
            toast.error("Missing project or story ID");
            return;
        }

        openDialog('form', {
            title: "Create New Chapter",
            description: "Create a new chapter with description and outline",
            fields: [
                { name: "name", label: "Chapter Name", type: "text", placeholder: "Chapter 1: The Beginning", required: true },
                { name: "description", label: "Description / Outline", type: "textarea", placeholder: "Describe what happens in this chapter..." }
            ],
            submitText: "Create Chapter"
        }, async (data: { name: string; description: string }) => {
            try {
                await createChapterMutation.mutateAsync({
                    storyId: parseInt(storyId),
                    title: data.name,
                    description: data.description,
                    projectId
                });

                toast.success('Chapter created successfully');
            } catch (error) {
                console.error('Error creating chapter:', error);
                toast.error('Failed to create chapter');
            }
        });
    }, [projectId, storyId, createChapterMutation, openDialog]);

    const handleEditChapter = useCallback(async (id: number | string, title: string) => {
        if (!projectId) return;

        const chapter = memoizedChapters.find(c => c.id === id);
        if (!chapter) return;

        openDialog('chapter', {
            defaultValues: {
                name: title,
                description: chapter.description || '',
                outline: '' // Add outline if available in chapter data
            },
            isEditing: true,
            chapterId: typeof id === 'string' ? parseInt(id) : id
        }, async (data: { name: string; description: string }) => {
            try {
                await updateChapterMutation.mutateAsync({
                    chapterId: typeof id === 'string' ? parseInt(id) : id,
                    title: data.name,
                    description: data.description,
                    projectId: projectId
                });

                toast.success('Chapter updated successfully');
            } catch (error) {
                console.error('Error updating chapter:', error);
                toast.error('Failed to update chapter');
            }
        });
    }, [projectId, memoizedChapters, openDialog, updateChapterMutation]);

    const handleDeleteChapter = useCallback(async (chapterId: number) => {
        if (!storyId || !projectId) return;

        if (confirm('Are you sure you want to delete this chapter? This cannot be undone.')) {
            try {
                await deleteChapterMutation.mutateAsync({
                    chapterId,
                    storyId,
                    projectId
                });
                toast.success('Chapter deleted');

                // If deleted chapter is active, navigate to project overview
                if (activeChapterId === chapterId.toString()) {
                    navigate(`/projects/${projectId}`);
                }
            } catch (error) {
                console.error('Error deleting chapter:', error);
                toast.error('Failed to delete chapter');
            }
        }
    }, [storyId, projectId, deleteChapterMutation, activeChapterId, navigate]);

    if (isLoading) {
        return <div className="px-4 py-3 text-xs text-muted-foreground/60 animate-pulse">Loading chapters...</div>;
    }

    if (memoizedChapters.length === 0) {
        return (
            <div className="px-2 py-4">
                <div
                    className="flex flex-col items-center justify-center p-3 text-center border border-dashed rounded-lg border-muted-foreground/20 hover:border-primary/50 hover:bg-muted/30 transition-all duration-300 group cursor-pointer"
                    onClick={handleAddChapter}
                >
                    <div className="p-1.5 mb-1.5 rounded-full bg-muted group-hover:bg-primary/10 transition-colors">
                        <Plus className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary" />
                    </div>
                    <p className="text-xs font-medium text-muted-foreground group-hover:text-primary transition-colors">Add first chapter</p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-1 py-1">
            {memoizedChapters.map((chapter) => {
                const isActive = activeChapterId === chapter.id.toString();
                return (
                    <ContextMenu key={chapter.id}>
                        <ContextMenuTrigger>
                            <SidebarMenuSubItem>
                                <div
                                    onClick={() => navigate(`/projects/${projectId}/chapters/${chapter.id}`)}
                                    className={cn(
                                        "w-full flex items-center justify-between px-3 py-2.5 rounded-md text-left text-sm transition-all duration-200 group relative select-none cursor-pointer",
                                        isActive
                                            ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                                            : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-foreground"
                                    )}
                                >
                                    <span className="truncate flex-1">{chapter.title}</span>

                                    {/* Hover Action - Clean & Subtle */}
                                    <div className={cn(
                                        "opacity-0 transition-opacity duration-200",
                                        "group-hover:opacity-100"
                                    )}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button className="h-6 w-6 flex items-center justify-center rounded-sm hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors">
                                                    <MoreHorizontal className="w-3.5 h-3.5" />
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-48">
                                                <DropdownMenuItem onClick={() => handleEditChapter(chapter.id, chapter.title || '')}>
                                                    <Edit className="mr-2 h-4 w-4" />
                                                    Edit Details
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                                <DropdownMenuItem
                                                    className="text-destructive focus:text-destructive"
                                                    onClick={() => handleDeleteChapter(chapter.id)}
                                                >
                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                    Delete Chapter
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </div>
                            </SidebarMenuSubItem>
                        </ContextMenuTrigger>
                        <ContextMenuContent className="w-56">
                            <ContextMenuItem onClick={() => handleEditChapter(chapter.id, chapter.title || '')}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit Details
                            </ContextMenuItem>
                            <ContextMenuSeparator />
                            <ContextMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDeleteChapter(chapter.id)}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete Chapter
                            </ContextMenuItem>
                        </ContextMenuContent>
                    </ContextMenu>
                );
            })}

        </div>
    );
}
